import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import { Button, Grid, IGridProps, Input, LoadingSpinner, Select, TabbedNav, UI } from '@gik/ui';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import arrayMove from 'array-move';
import { GiftCardTile } from './GiftCardTile';
import noop from '@gik/core/utils/noop';
import { useGiftcardCategories } from '@gik/api/products/giftcardCategories';
import { useGiftcardProductsByCategoryId } from '@gik/api/products/giftcardsByCategoryId';
import { Product } from '@gik/core/models/gik/Product';
import { fuzzysearch } from '@gik/core/utils/string';
import { renderPortal } from '@gik/core/utils/RenderPortal';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';
import { translationKeys as commonTranslationKeys } from '@gik/i18n/en/common';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';

export interface IGiftCardsEditorProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  maxItems?: number;
  value?: Product[];
  onChange?: (value: Product[]) => void;
  onSave?: (value: Product[]) => void;
  buttonsPortal?: () => HTMLElement;
}

export function GiftCardsEditor({
  onChange = noop,
  onSave = noop,
  className,
  value = [],
  maxItems = 4,
  buttonsPortal,
  ...otherProps
}: IGiftCardsEditorProps): React.ReactElement {
  const bem = bemBlock('giftcards-editor');
  const { t } = useTranslation();
  const isMd = useBreakpoint(Breakpoint.MD);

  const maxCards = 4;

  const [activeTabId, setActiveTabId] = React.useState<number>();
  const [selectedCards, setSelectedCards] = React.useState<Product[]>(value);
  const [cachedGiftCards, setCachedGiftCards] = React.useState<Product[]>(value);
  const [searchQuery, setSearchQuery] = React.useState<string>();
  const [ready, setReady] = React.useState<boolean>();

  const { data: giftCardCategories } = useGiftcardCategories();
  const { data: giftCards } = useGiftcardProductsByCategoryId(0); // just use the all category and filter at frontend

  function onSortEnd({ oldIndex, newIndex }) {
    const newSel = arrayMove(selectedCards, oldIndex, newIndex);
    setSelectedCards(newSel);
    onChange(newSel);
  }

  function handleDelete(item: Product) {
    // do not allow the last card to be deleted
    if (selectedCards.length === 1) return;

    const newSel = selectedCards.filter(card => card.id !== item.id);
    setSelectedCards(newSel);
    onChange(newSel);
  }

  function handleAdd(item: Product) {
    if (selectedCards.length >= maxItems) {
      return UI.notifyError(t(translationKeys.maxCardsNotification));
    }
    const newSel = selectedCards.concat([item]);
    setSelectedCards(newSel);
    onChange(newSel);
  }

  function handleSave() {
    // saving an empty list of giftcards should not be allowed
    if (!selectedCards.length) {
      UI.notifyError(t(translationKeys.minCardsNotification));
      return;
    }
    onSave(selectedCards);
  }

  React.useEffect(() => {
    // select the most popular category by default
    const mostPopularTabId = 41862;
    if (giftCardCategories && activeTabId === undefined) setActiveTabId(mostPopularTabId);
  }, [giftCardCategories, activeTabId]);

  React.useEffect(() => {
    if (giftCards) setCachedGiftCards(giftCards);
  }, [giftCards]);

  React.useEffect(() => {
    // this timeout is here to prevent the drawer animation from stuttering
    // let the drawer animate first before we start rendering the content
    setTimeout(() => {
      setReady(true);
    }, 300);
  }, []);

  const isLoading = !giftCardCategories || !ready;

  if (isLoading) return <LoadingSpinner center />;

  const categoriesSorted = giftCardCategories
    ?.concat([
      {
        id: 0,
        name: t(translationKeys.allCategoryName),
        acf: {
          sort_order: '0',
        },
      },
    ])
    .sort((a, b) => {
      return parseInt(a.acf.sort_order) - parseInt(b.acf.sort_order);
    });

  const navItems = categoriesSorted.map(item => ({
    title: item.name,
    id: item.id,
  }));

  const navOptions = categoriesSorted.map(item => ({
    label: item.name,
    value: item.id,
  }));

  const SortableGrid = SortableContainer<IGridProps>(Grid);
  const SortableItem = SortableElement(({ item }: { item: Product }) => {
    return (
      <GiftCardTile
        giftcard={item}
        className={bem('drag-item')}
        deleteButton={selectedCards.length > 1}
        onDelete={handleDelete}
      />
    );
  });

  // strip out selection from giftCards to get the remaining giftcards to be displayed
  const remainingItems: Product[] = [];
  let currentGiftcards = cachedGiftCards.concat([]);

  // filter by search query
  if (searchQuery) {
    currentGiftcards = currentGiftcards.filter(item => fuzzysearch(searchQuery, item.name));
  }

  // filter by category
  if (activeTabId !== 0) {
    currentGiftcards = currentGiftcards.filter(item => item.giftCardCategoryIds?.indexOf(activeTabId) > -1);
  }

  currentGiftcards.forEach(item => {
    const inSelection = selectedCards?.find(card => card.id === item.id);
    if (inSelection === undefined) remainingItems.push({ ...item });
  });

  const buttons = (
    <div className={'gik-drawer-actions gik-drawer-actions--centered'}>
      <Button onClick={handleSave}>{t(commonTranslationKeys.save)}</Button>
    </div>
  );

  let emptyCardsCount = maxCards;
  if (selectedCards) emptyCardsCount = maxCards - selectedCards?.length;

  return (
    <div className={bem(null, null, className)} {...otherProps}>
      <div className={bem('selection')}>
        <span className={bem('sel-title')}>{t(translationKeys.editorPickerTitle)}</span>
        <p className={bem('sel-description')}>{t(translationKeys.editorPickerDescription)}</p>
        <SortableGrid className={bem('sel-grid')} distance={10} axis="xy" onSortEnd={onSortEnd}>
          {selectedCards?.map((item, index: number) => {
            return <SortableItem key={item.id} index={index} item={item} />;
          })}
          {emptyCardsCount > 0 &&
            [...Array(emptyCardsCount)].map(count => {
              return (
                <div key={count} className={bem('empty-tile')}>
                  <main></main>
                  <footer>hidden</footer>
                </div>
              );
            })}
        </SortableGrid>
        <div className={bem('counter-wrapper')}>
          <div className={bem('counter')}>
            {selectedCards?.length}/{maxItems}
          </div>
        </div>
      </div>
      <div className={bem('browser')}>
        <div className={bem('filters')}>
          <Input
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder={t(translationKeys.searchPlaceholder)}
          />
        </div>

        {isMd && navItems && (
          <TabbedNav value={activeTabId} onChange={_item => setActiveTabId(_item.id)} items={navItems} />
        )}
        {!isMd && (
          <Select<number>
            className={bem('select')}
            loading={!navItems}
            options={navOptions}
            value={activeTabId}
            onChange={value => setActiveTabId(value)}
          />
        )}

        {giftCards ? (
          <div className={bem('grid-wrapper')}>
            <Grid className={bem('grid')}>
              {remainingItems?.map(item => {
                return (
                  <GiftCardTile
                    giftcard={item}
                    key={item.id}
                    addButton
                    onAdd={() => handleAdd(item)}
                    onClickItem={() => handleAdd(item)}
                  />
                );
              })}
            </Grid>
            {remainingItems?.length === 0 && (
              <div className={bem('grid-empty')}>{t(translationKeys.noGiftCardsInFilter)}</div>
            )}
          </div>
        ) : (
          <LoadingSpinner center />
        )}
      </div>

      {renderPortal(buttons, buttonsPortal)}
    </div>
  );
}
